import { ReactNode } from 'react';
import Image from 'next/image';
import { useTranslation } from 'react-i18next';

import { Box, Flex, Heading, Text } from '@chakra-ui/react';

import { ButtonLink } from '~/components/button-link';
import { trackEvent } from '~/features/analytics/track-event';
import { HeroLayout } from '~/features/experiments/ab_plp_redesign_2025/components/hero-layout';
import { MessageIcon, RocketIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';

export type FooterBannerSectionProps = {
  title?: ReactNode;
  description?: ReactNode;
  ctaSlot?: ReactNode;
};

export function FooterBannerSection({ title, description, ctaSlot }: FooterBannerSectionProps = {}) {
  const { t } = useTranslation();
  return (
    <HeroLayout
      backgroundImageOverlayGradient={{
        md: 'radial(circle,rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0) 25%, rgba(0, 0, 0, 0) 70%, rgb(0, 0, 0) 100%)',
      }}
      backgroundImageSlot={
        <>
          <Box display={{ base: 'block', md: 'none' }} inset={0} position='absolute'>
            <Image
              alt=''
              quality={100}
              sizes='100vw'
              src='/images/enterprise/man-thinking-bg-mobile.png'
              style={{ objectFit: 'cover', objectPosition: 'top', minWidth: '458px' }}
              fill
              priority
            />
          </Box>
          <Box bottom='-2px' display={{ base: 'none', md: 'block' }} left={0} position='absolute' right={0} top={0}>
            <Image
              alt=''
              loading='lazy'
              quality={80}
              sizes='(max-width: 1023px) 1000px, 100vw'
              src='/images/enterprise/man-thinking-bg.png'
              style={{ objectFit: 'cover', objectPosition: 'right' }}
              fill
            />
          </Box>
        </>
      }
      contentContainerProps={{
        padding: {
          base: '274px 16px 16px',
          md: 4,
        },
      }}
      ctaSlot={
        ctaSlot ?? (
          <>
            <ButtonLink
              buttonProps={{
                leftIcon: <RocketIcon height='20px' width='20px' />,
                flex: {
                  base: 1,
                  md: 'unset',
                },
                onClick: () =>
                  trackEvent({
                    name: 'Enterprise CTA Clicked',
                    type: 'click',
                    label: 'demo_skills_clicked',
                  }),
              }}
              linkProps={{
                href: '/get-started/enterprise',
              }}
            >
              {t('enterprise.overview.cta.demoSkills')}
            </ButtonLink>
            <ButtonLink
              buttonProps={{
                variant: 'outline',
                colorScheme: 'white',
                leftIcon: <MessageIcon height='20px' width='20px' />,
                flex: {
                  base: 1,
                  md: 'unset',
                },
                onClick: () =>
                  trackEvent({
                    name: 'Enterprise CTA Clicked',
                    type: 'click',
                    label: 'contact_us_clicked',
                  }),
              }}
              linkProps={{
                href: '/get-started/enterprise',
              }}
            >
              {t('common.contactUs')}
            </ButtonLink>
          </>
        )
      }
      minHeight={{
        md: '480px',
      }}
      textContentSlot={
        <Flex
          flexDirection='column'
          gap={4}
          whiteSpace='pre-wrap'
          maxWidth={{
            base: '253px',
            md: '638px',
          }}
        >
          <Heading
            as='h2'
            color='white'
            fontWeight='600'
            letterSpacing='-1px'
            lineHeight='1.1'
            fontSize={{
              base: '2.375rem',
              md: '3.375rem',
            }}
          >
            {title ?? t('enterprise.overview.footerBanner.title')}
          </Heading>
          <Text color='gray.300' fontSize='lg' lineHeight='lg'>
            {description ?? t('enterprise.overview.footerBanner.description')}
          </Text>
        </Flex>
      }
    />
  );
}
